import { brandData } from '../data/brand-data.js';
import { renderStrategyFeedbackWidget } from './inline-feedback.js';

export function renderStrategyView() {
  const { coreValues, missionStatement } = brandData;

  const sayThisNotThat = [
    { not: "All are welcome (with fine print).", say: "Radically inclusive; fully affirming LGBTQIA+ people without exception.", why: "Eliminates ambiguity and signals genuine safety for queer seekers." },
    { not: "Biblical truth without compromise.", say: "Rooted in the liberating way of Jesus that tears down walls.", why: "Shifts from authoritarian literalism to Christ-like compassion and justice." },
    { not: "Come get plugged into our programs.", say: "Find your people in the village.", why: "Replaces corporate church jargon with warm, relational hospitality." },
    { not: "Do you have a personal relationship with Jesus?", say: "Following Jesus into the work of love and justice.", why: "Reframes faith as embodied discipleship rather than an individual cognitive test." },
    { not: "Sunday obligation / church duty.", say: "A sanctuary of rest and an open table for all.", why: "Replaces religious guilt with restorative spiritual grace." }
  ];

  return `
    <section class="view-section" id="view-strategy">
      <div class="hero-card">
        <span class="hero-tag">Phase 03 Strategic Architecture</span>
        <h2>Brand Positioning & Messaging Framework</h2>
        <p style="max-width: 800px; font-size: var(--text-lg); margin-bottom: 0;">
          The strategic blueprint: establishing UVC's official mission, the <strong>Sanctuary & Catalyst</strong> positioning model, the 3 Core Values operational rubric, and tiered messaging playbook.
        </p>
      </div>

      <!-- Official Mission & Positioning Statement -->
      <div class="card" style="margin-bottom: var(--space-2xl); background: linear-gradient(180deg, rgba(29, 33, 48, 0.95) 0%, rgba(20, 23, 34, 0.95) 100%); border-left: 4px solid var(--accent-primary);">
        <div style="color: var(--accent-primary); font-size: var(--text-xs); font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 4px;">Official Mission Statement</div>
        <blockquote style="font-size: var(--text-2xl); font-weight: 800; line-height: 1.4; color: var(--text-primary); margin-bottom: var(--space-md);">
          "${missionStatement}"
        </blockquote>
        <div style="font-size: var(--text-sm); color: var(--text-secondary); line-height: 1.7;">
          <strong>Strategic Positioning:</strong> For urban Chicagoans seeking spiritual depth without dogma or exclusion, Urban Village Church is the progressive multi-site Christian community that creates Jesus-loving, radically inclusive neighborhood gatherings offering both restorative personal sanctuary and prophetic civic justice.
        </div>
      </div>

      <!-- The Sanctuary & Catalyst Dual Engine -->
      <h3 style="margin-bottom: var(--space-lg);">Strategic Synthesis: The Dual Engine</h3>
      <div class="grid-2" style="margin-bottom: var(--space-2xl);">
        <div class="card" style="border-top: 3px solid #10b981;">
          <div class="card-header">
            <h4 style="color: var(--text-primary); margin: 0; font-size: var(--text-xl);">1. The Sanctuary (Inward Rest)</h4>
            <span class="hero-tag" style="background: rgba(16, 185, 129, 0.15); color: #34d399; margin: 0;">Trauma-Informed</span>
          </div>
          <p style="font-size: var(--text-sm); line-height: 1.7; color: var(--text-secondary); margin: 0;">
            A safe, restorative haven for deconstructing seekers, burnout professionals, and queer families. Space to breathe, doubt honestly, and experience unconditional grace without performance or guilt.
          </p>
        </div>

        <div class="card" style="border-top: 3px solid #6366f1;">
          <div class="card-header">
            <h4 style="color: var(--text-primary); margin: 0; font-size: var(--text-xl);">2. The Catalyst (Outward Impact)</h4>
            <span class="hero-tag" style="background: rgba(99, 102, 241, 0.15); color: #818cf8; margin: 0;">Prophetic Action</span>
          </div>
          <p style="font-size: var(--text-sm); line-height: 1.7; color: var(--text-secondary); margin: 0;">
            A bold, courageous urban movement that translates faith into anti-racist advocacy, mutual aid, and civic partnerships—igniting the city and beyond through tangible public solidarity.
          </p>
        </div>
      </div>

      <!-- The 3 Core Value Pillars -->
      <h3 style="margin-bottom: var(--space-lg);">The 3 Core Value Pillars & Operational Rubrics</h3>
      <div class="grid-3" style="margin-bottom: var(--space-2xl);">
        ${coreValues.map(val => `
          <div class="card">
            <div class="card-header">
              <h4 style="margin: 0; font-size: var(--text-2xl); color: var(--text-primary);">${val.title}</h4>
              <span class="hero-tag" style="margin: 0;">${val.badge}</span>
            </div>
            <div style="font-weight: 600; color: #a5b4fc; font-size: var(--text-sm); margin-bottom: var(--space-sm);">${val.summary}</div>
            <p style="font-size: var(--text-xs); margin: 0; line-height: 1.7;">${val.description}</p>
          </div>
        `).join('')}
      </div>

      <!-- Tiered Elevator Pitches -->
      <div class="card" style="margin-bottom: var(--space-2xl);">
        <h3 class="card-title" style="margin-bottom: var(--space-md);">Tiered Elevator Pitches</h3>
        <div style="display: flex; flex-direction: column; gap: var(--space-md);">
          <div style="background: var(--bg-surface-elevated); padding: var(--space-md); border-radius: var(--radius-md);">
            <div class="mono" style="font-size: var(--text-xs); color: var(--accent-primary); font-weight: 700; margin-bottom: 4px;">10-SECOND STREET HOOK</div>
            <p style="font-size: var(--text-sm); color: var(--text-primary); margin: 0; line-height: 1.6;">
              "Urban Village Church is a bold, inclusive, and relevant progressive faith community in Chicago creating Jesus-loving spaces that ignite the city and beyond."
            </p>
          </div>
          <div style="background: var(--bg-surface-elevated); padding: var(--space-md); border-radius: var(--radius-md);">
            <div class="mono" style="font-size: var(--text-xs); color: var(--accent-gold); font-weight: 700; margin-bottom: 4px;">30-SECOND CONVERSATIONAL OVERVIEW</div>
            <p style="font-size: var(--text-sm); color: var(--text-primary); margin: 0; line-height: 1.6;">
              "We’re an intentional, multi-site progressive church in Chicago rooted in three core values: Bold, Inclusive, and Relevant. Whether you’re LGBTQIA+, healing from past church trauma, asking tough questions, or looking for real community and social justice, you have a seat at our table."
            </p>
          </div>
          <div style="background: var(--bg-surface-elevated); padding: var(--space-md); border-radius: var(--radius-md);">
            <div class="mono" style="font-size: var(--text-xs); color: var(--accent-emerald); font-weight: 700; margin-bottom: 4px;">60-SECOND VISION MANIFESTO</div>
            <p style="font-size: var(--text-sm); color: var(--text-primary); margin: 0; line-height: 1.6;">
              "In a city as dynamic and diverse as Chicago, faith shouldn't mean checking your mind or your identity at the door. At Urban Village Church, we exist to create Jesus-loving, inclusive communities that ignite the city and beyond across Wicker Park, Hyde Park, Ravenswood, and River Forest. No matter who you are, who you love, or where you are on your spiritual journey: welcome home to the village."
            </p>
          </div>
        </div>
      </div>

      <!-- Say This / Not That Language Playbook -->
      <div class="card" style="margin-bottom: var(--space-2xl);">
        <h3 class="card-title" style="margin-bottom: var(--space-md);">"Say This / Not That" Language Playbook</h3>
        <div style="display: flex; flex-direction: column; gap: var(--space-sm);">
          ${sayThisNotThat.map(item => `
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: var(--space-md); background: var(--bg-surface-elevated); padding: var(--space-md); border-radius: var(--radius-md); border: 1px solid var(--border-subtle);">
              <div>
                <div style="font-size: 10px; color: var(--accent-rose); font-weight: 700; text-transform: uppercase;">Avoid (Dated / Exclusive / Cliché):</div>
                <div style="font-size: var(--text-xs); color: var(--text-secondary); text-decoration: line-through; margin-top: 2px;">${item.not}</div>
              </div>
              <div>
                <div style="font-size: 10px; color: var(--accent-emerald); font-weight: 700; text-transform: uppercase;">Say This (Bold, Inclusive, Relevant):</div>
                <div style="font-size: var(--text-xs); color: var(--text-primary); font-weight: 600; margin-top: 2px;">${item.say}</div>
              </div>
            </div>
          `).join('')}
        </div>
      </div>

      <!-- Embedded Feedback Widget -->
      ${renderStrategyFeedbackWidget()}
    </section>
  `;
}
